import {
  AfterViewChecked, Component, ElementRef, HostListener, ViewChild,
  ViewEncapsulation, computed, inject, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { getAuth, onAuthStateChanged, Unsubscribe } from 'firebase/auth';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { marked } from 'marked';
import { FirebaseService } from '../../services/firebase/firebase.service';
import { DataStoreService } from '../../services/data-store/data-store.service';
import { SupportChatbotService } from './support-chatbot.service';
import { environment } from '../../../environments/environment';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const FUNCTIONS_REGION = 'northamerica-northeast1';

@Component({
  selector: 'app-support-chatbot',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoModule],
  styles: [`
    :host { display: contents; }
    .sc-panel {
      position: fixed; z-index: 1000;
      width: 22rem; max-width: calc(100vw - 2rem);
      height: 28rem; max-height: calc(100vh - 6rem);
      background: var(--background-color, #fff); color: var(--text-color, #111);
      border: 1px solid var(--border-color, #e5e7eb);
      border-radius: 12px; box-shadow: 0 25px 50px rgba(0,0,0,.25);
      display: flex; flex-direction: column; overflow: hidden;
    }
    .sc-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid var(--border-color, #e5e7eb); font-weight:600; font-size:14px; line-height:20px; user-select:none; }
    .sc-header button { background:none; border:none; cursor:pointer; color:inherit; padding:4px; border-radius:4px; }
    .sc-header button:hover { background: var(--surface-color, #f3f4f6); }
    .sc-body { flex:1; overflow-y:auto; padding:12px 14px; display:flex; flex-direction:column; gap:10px; font-size:14px; }
    .sc-body-centered { justify-content:center; align-items:center; }
    .sc-empty { text-align:center; color: var(--text-secondary-color, #6b7280); margin-top:32px; line-height:20px; }
    .sc-no-credits { text-align:center; padding:0 16px; display:flex; flex-direction:column; align-items:center; gap:16px; }
    .sc-no-credits p { color: var(--text-secondary-color, #6b7280); margin:0; line-height:20px; }
    .sc-buy-credits {
      display:inline-flex; align-items:center; justify-content:center;
      background:#4c17ca; color:#fff !important; text-decoration:none;
      padding:8px 16px; border-radius:8px; font-size:14px; font-weight:500;
      transition: background-color .15s; cursor:pointer;
    }
    .sc-buy-credits:hover { background:#3d12a2; }

    /* User = grey bubble on surface-color. Assistant = no bubble, plain text. */
    .sc-row { display:flex; }
    .sc-row.user { justify-content:flex-end; }
    .sc-bubble-user {
      display:inline-block; max-width:85%; padding:8px 12px; border-radius:1rem;
      background: var(--surface-color, #f3f4f6); color: var(--text-color, #111);
      white-space:pre-wrap; word-break:break-word; font-size:14px; line-height:1.5;
    }
    .sc-assistant { max-width:100%; color: var(--text-color, #111); font-size:14px; line-height:1.5; }
    .sc-assistant :first-child { margin-top:0; }
    .sc-assistant :last-child  { margin-bottom:0; }
    .sc-assistant p { margin: 0.5rem 0; }
    .sc-assistant h1, .sc-assistant h2, .sc-assistant h3,
    .sc-assistant h4, .sc-assistant h5, .sc-assistant h6 {
      font-weight: 600; margin: 0.85rem 0 0.35rem; line-height: 1.3;
    }
    .sc-assistant h1 { font-size: 1.1rem; }
    .sc-assistant h2 { font-size: 1.02rem; }
    .sc-assistant h3 { font-size: 0.95rem; }
    .sc-assistant h4, .sc-assistant h5, .sc-assistant h6 { font-size: 0.9rem; }
    .sc-assistant ul, .sc-assistant ol {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }
    .sc-assistant ul { list-style: disc outside; }
    .sc-assistant ol { list-style: decimal outside; }
    .sc-assistant ul ul { list-style: circle outside; }
    .sc-assistant ul ul ul { list-style: square outside; }
    .sc-assistant ul ul, .sc-assistant ol ol,
    .sc-assistant ul ol, .sc-assistant ol ul {
      margin: 0.25rem 0;
    }
    .sc-assistant li { margin: 0.2rem 0; padding-left: 0.15rem; }
    .sc-assistant li > p { margin: 0.2rem 0; }
    .sc-assistant li::marker { color: var(--text-secondary-color, #6b7280); }
    .sc-assistant a { color: #4c17ca; text-decoration: underline; text-underline-offset: 2px; }
    .sc-assistant a:hover { text-decoration-thickness: 2px; }
    .sc-assistant strong { font-weight: 600; }
    .sc-assistant em { font-style: italic; }
    .sc-assistant code {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.85em;
      background: rgba(127, 127, 127, 0.18);
      padding: 0.1em 0.35em;
      border-radius: 0.25rem;
    }
    .sc-assistant pre {
      background: rgba(127, 127, 127, 0.15);
      padding: 0.65rem 0.75rem;
      border-radius: 0.5rem;
      overflow-x: auto;
      margin: 0.6rem 0;
      font-size: 0.85em;
      line-height: 1.5;
    }
    .sc-assistant pre code {
      background: transparent;
      padding: 0;
      font-size: 1em;
    }
    .sc-assistant blockquote {
      border-left: 3px solid rgba(127, 127, 127, 0.4);
      padding: 0.1rem 0 0.1rem 0.65rem;
      margin: 0.5rem 0;
      color: var(--text-secondary-color, #6b7280);
    }
    .sc-assistant hr { border: none; border-top: 1px solid rgba(127, 127, 127, 0.3); margin: 0.75rem 0; }
    .sc-assistant table {
      border-collapse: collapse;
      margin: 0.5rem 0;
      font-size: 0.9em;
      width: 100%;
    }
    .sc-assistant th, .sc-assistant td {
      border: 1px solid rgba(127, 127, 127, 0.3);
      padding: 0.3rem 0.5rem;
      text-align: left;
    }
    .sc-assistant th {
      background: rgba(127, 127, 127, 0.1);
      font-weight: 600;
    }

    .sc-error { color:#ef4444; font-size:12px; }
    .sc-notice { font-size:12px; color: var(--text-secondary-color, #6b7280); padding:8px; }
    .sc-notice a { color:#4c17ca; font-weight:500; }

    /* Footer has NO top border — input container carries the border. */
    .sc-footer { padding:8px; }
    .sc-input-wrap {
      display:flex; align-items:flex-end; gap:8px;
      width:100%; padding: 6px 8px;
      border:1px solid var(--border-color, #e5e7eb); border-radius:0.5rem;
      background: color-mix(in srgb, var(--surface-color, #f3f4f6) 50%, transparent);
      backdrop-filter: blur(4px);
      transition: border-color 0.15s;
    }
    .sc-input-wrap:hover,
    .sc-input-wrap:focus-within {
      border-color: color-mix(in srgb, var(--text-secondary-color, #6b7280) 40%, transparent);
    }
    .sc-input-wrap textarea {
      flex:1; resize:none; border:none; background:transparent;
      color: var(--text-color, #111); font-size:14px; padding:4px 0; max-height:160px; outline:none;
      font-family:inherit; line-height:1.375;
    }
    .sc-input-wrap textarea::placeholder {
      color: color-mix(in oklab, var(--text-secondary-color, #6b7280) 60%, transparent);
    }

    .sc-send {
      background:#4c17ca; color:#fff; border:none; border-radius:9999px;
      width:32px; height:32px; display:inline-flex; align-items:center; justify-content:center;
      cursor:pointer; transition: background-color .15s; padding:0; flex-shrink:0;
    }
    .sc-send:hover:not(:disabled) { background:#3d12a2; }
    .sc-send:active:not(:disabled) { background:#2e0e7a; }
    .sc-send:disabled { background: var(--border-color, #e5e7eb); color: var(--text-secondary-color, #6b7280); cursor: default; }
    .sc-send svg { width:20px; height:20px; }
    .sc-send .sc-spinner {
      width:16px; height:16px; border-radius:9999px;
      border:2px solid currentColor; border-top-color: transparent;
      animation: sc-spin 1s linear infinite;
    }
    @keyframes sc-spin { to { transform: rotate(360deg); } }

    /* Typing indicator bouncing dots. */
    .sc-typing { display:inline-flex; align-items:center; gap:4px; vertical-align:middle; }
    .sc-typing-dot {
      display:inline-block; width:6px; height:6px; border-radius:9999px;
      background-color: var(--text-secondary-color, #6b7280);
      animation: sc-typing 1.4s infinite;
    }
    .sc-typing-dot:nth-child(2) { animation-delay: 0.2s; }
    .sc-typing-dot:nth-child(3) { animation-delay: 0.4s; }
    @keyframes sc-typing {
      0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
      30%           { opacity: 1;   transform: translateY(-2px); }
    }
  `],
  encapsulation: ViewEncapsulation.None,
  template: `
    <ng-container *transloco="let t">
      @if (authed()) {
        @if (open()) {
          <div #panel class="sc-panel" [ngStyle]="panelStyle()">
            <div class="sc-header">
              <span>{{ t('support-chat.title') }}</span>
              <div style="display:flex;gap:4px;">
                @if (sending()) {
                  <button type="button" (click)="cancel()" [attr.aria-label]="t('support-chat.stop')" style="font-size:12px;padding:2px 8px;cursor:pointer;">
                    {{ t('support-chat.stop') }}
                  </button>
                }
                <button type="button" (click)="toggle()" [attr.aria-label]="t('support-chat.closeAria')" style="cursor:pointer;">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
                </button>
              </div>
            </div>
            <div #scroll class="sc-body" [class.sc-body-centered]="messages().length === 0 && hasOrg() && !hasCredits()">
              @if (messages().length === 0 && hasOrg() && hasCredits()) {
                <div class="sc-empty">{{ t('support-chat.greeting') }}</div>
              } @else if (messages().length === 0 && hasOrg() && !hasCredits()) {
                <div class="sc-no-credits">
                  <p>{{ t('support-chat.noCredits') }}</p>
                  <a class="sc-buy-credits" [href]="adminConsoleUrl + '/billing'" target="_blank" rel="noopener">
                    {{ t('support-chat.buyCredits') }}
                  </a>
                </div>
              }
              @for (msg of messages(); track $index) {
                @if (msg.role === 'user') {
                  <div class="sc-row user">
                    <div class="sc-bubble-user">{{ msg.content }}</div>
                  </div>
                } @else {
                  <div class="sc-assistant">
                    @if (msg.content) {
                      <div [innerHTML]="renderMarkdown(msg.content)"></div>
                    } @else if (sending()) {
                      <span class="sc-typing" aria-hidden="true">
                        <span class="sc-typing-dot"></span>
                        <span class="sc-typing-dot"></span>
                        <span class="sc-typing-dot"></span>
                      </span>
                    }
                  </div>
                }
              }
              @if (errorMsg()) { <div class="sc-error">{{ errorMsg() }}</div> }
            </div>
            <div class="sc-footer">
              @if (!hasOrg()) {
                <div class="sc-notice">
                  {{ t('support-chat.noOrg') }}
                  <a [href]="adminConsoleUrl + '/new'" target="_blank" rel="noopener">
                    {{ t('support-chat.noOrgCta') }}
                  </a>
                </div>
              } @else {
                <div class="sc-input-wrap">
                  <textarea
                    #ta
                    [(ngModel)]="input"
                    (keydown)="onKeydown($event)"
                    (input)="autoGrow(ta)"
                    [disabled]="sending() || !hasCredits()"
                    rows="1"
                    [placeholder]="t('support-chat.placeholder')"
                  ></textarea>
                  <button type="button" class="sc-send" (click)="send(ta)" [disabled]="sending() || !input.trim() || !hasCredits()" [attr.aria-label]="t('support-chat.send')">
                    @if (sending()) {
                      <span class="sc-spinner"></span>
                    } @else {
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 17a.75.75 0 0 1-.75-.75V5.56L5.22 9.59a.75.75 0 1 1-1.04-1.08l5.25-5.25a.75.75 0 0 1 1.04 0l5.25 5.25a.75.75 0 1 1-1.04 1.08L10.75 5.56v10.69A.75.75 0 0 1 10 17Z" clip-rule="evenodd" />
                      </svg>
                    }
                  </button>
                </div>
              }
            </div>
          </div>
        }
      }
    </ng-container>
  `,
})
export class SupportChatbotComponent implements AfterViewChecked {
  private firebase = inject(FirebaseService);
  private dataStore = inject(DataStoreService);
  private transloco = inject(TranslocoService);
  private supportChatbot = inject(SupportChatbotService);

  @ViewChild('scroll') scroll?: ElementRef<HTMLDivElement>;
  @ViewChild('panel') panel?: ElementRef<HTMLDivElement>;

  readonly adminConsoleUrl = environment.adminConsoleUrl;
  readonly open = this.supportChatbot.open;
  readonly messages = signal<ChatMessage[]>([]);
  readonly sending = signal(false);
  readonly errorMsg = signal('');
  readonly authed = signal(false);
  readonly orgId = signal<string | null>(null);
  readonly creditBalance = signal<number>(0);
  readonly hasOrg = computed(() => !!this.orgId());
  readonly hasCredits = computed(() => this.creditBalance() > 0);
  readonly panelPos = signal<{ x: number; y: number } | null>(null);
  readonly dragging = signal(false);
  readonly panelStyle = computed(() => {
    const p = this.panelPos();
    const style: { [key: string]: string } = p
      ? { left: `${p.x}px`, top: `${p.y}px` }
      : { bottom: '20px', right: '20px' };
    return style;
  });

  input = '';
  private shouldScroll = false;
  private sessionId: string | null = null;
  private abortController: AbortController | null = null;
  private dragPointerId: number | null = null;
  private dragOffset = { x: 0, y: 0 };
  private authUnsubscribe?: Unsubscribe;

  constructor() {
    this.authUnsubscribe = onAuthStateChanged(this.firebase.auth, (user) => {
      this.authed.set(!!user && !user.isAnonymous);
    });
    this.dataStore.getState$().subscribe(({ newState }) => {
      const org = newState.organization;
      this.orgId.set(org?.id ?? newState.currentAccount?.organizationId ?? null);
      this.creditBalance.set(Number(org?.creditBalance ?? 0));
    });
  }

  ngOnDestroy() {
    this.authUnsubscribe?.();
  }

  toggle() {
    const willOpen = !this.open();
    this.supportChatbot.toggle();
    if (willOpen) {
      // Snap back to the default bottom-right corner every time the panel opens,
      // regardless of where it was dragged during the last session.
      this.panelPos.set(null);
      if (!this.sessionId) this.sessionId = this.generateSessionId();
    } else {
      this.resetSession();
    }
    this.shouldScroll = true;
  }

  autoGrow(ta: HTMLTextAreaElement) {
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }

  ngAfterViewChecked() {
    if (this.shouldScroll && this.scroll) {
      this.scroll.nativeElement.scrollTop = this.scroll.nativeElement.scrollHeight;
      this.shouldScroll = false;
    }
  }

  onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  }

  renderMarkdown(text: string): string {
    return marked.parse(text, { async: false, breaks: true, gfm: true }) as string;
  }

  cancel() {
    this.abortController?.abort();
  }

  async send(ta?: HTMLTextAreaElement) {
    const text = this.input.trim();
    if (!text || this.sending() || !this.hasCredits()) return;
    if (!this.sessionId) this.sessionId = this.generateSessionId();

    this.errorMsg.set('');
    const next: ChatMessage[] = [
      ...this.messages(),
      { role: 'user', content: text },
      { role: 'assistant', content: '' },
    ];
    this.messages.set(next);
    this.input = '';
    if (ta) ta.style.height = '';
    this.sending.set(true);
    this.shouldScroll = true;

    try {
      await this.stream(text);
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        this.errorMsg.set(err?.message || this.transloco.translate('support-chat.error'));
      }
    } finally {
      this.sending.set(false);
      this.abortController = null;
      this.shouldScroll = true;
    }
  }

  private async stream(message: string): Promise<void> {
    const user = getAuth().currentUser;
    if (!user) {
      this.errorMsg.set(this.transloco.translate('support-chat.notAuthenticated'));
      return;
    }
    const idToken = await user.getIdToken();
    const projectId = environment.firebaseConfig.projectId;
    const endpoint = `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net/runSupportAgent`;

    this.abortController = new AbortController();
    const resp = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        sessionId: this.sessionId,
        message,
        app: 'forge',
        orgId: this.orgId() ?? undefined,
      }),
      signal: this.abortController.signal,
    });

    if (resp.status === 402) {
      throw new Error(this.transloco.translate('support-chat.noCredits'));
    }
    if (resp.status === 400) {
      throw new Error(this.transloco.translate('support-chat.noOrg'));
    }
    if (!resp.ok || !resp.body) {
      const body = await resp.text().catch(() => '');
      throw new Error(`Server ${resp.status}: ${body || resp.statusText}`);
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 2);
        for (const line of frame.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            this.handleEvent(event);
          } catch {
            // Ignore malformed frame
          }
        }
      }
    }
  }

  private handleEvent(event: any): void {
    const type = event?.type as string | undefined;
    if (!type) return;

    if (type === 'token' && typeof event.token === 'string') {
      this.appendToLastAssistant(event.token);
      this.shouldScroll = true;
      return;
    }

    if (type === 'error' && typeof event.error === 'string') {
      const code = event.error;
      const knownKey = `support-chat.${code}`;
      const translated = this.transloco.translate(knownKey);
      this.errorMsg.set(translated && translated !== knownKey ? translated : code);
      return;
    }
    // 'run-started', 'done' — no UI state needed here.
  }

  private appendToLastAssistant(chunk: string) {
    const msgs = this.messages();
    const last = msgs[msgs.length - 1];
    if (!last || last.role !== 'assistant') {
      this.messages.set([...msgs, { role: 'assistant', content: chunk }]);
      return;
    }
    const updated: ChatMessage = { role: 'assistant', content: last.content + chunk };
    this.messages.set([...msgs.slice(0, -1), updated]);
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  private resetSession() {
    this.abortController?.abort();
    this.abortController = null;
    this.sessionId = null;
    this.messages.set([]);
    this.errorMsg.set('');
    this.sending.set(false);
  }

  // ── Drag ──

  onHeaderPointerDown(event: PointerEvent) {
    if ((event.target as HTMLElement).closest('button')) return;
    const panel = this.panel?.nativeElement;
    if (!panel) return;
    event.preventDefault();
    const rect = panel.getBoundingClientRect();
    this.dragOffset = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    this.dragPointerId = event.pointerId;
    this.panelPos.set(this.clamp(rect.left, rect.top, rect.width, rect.height));
    this.dragging.set(true);
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent) {
    if (!this.dragging() || event.pointerId !== this.dragPointerId) return;
    const panel = this.panel?.nativeElement;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const x = event.clientX - this.dragOffset.x;
    const y = event.clientY - this.dragOffset.y;
    this.panelPos.set(this.clamp(x, y, rect.width, rect.height));
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  onPointerUp(event: PointerEvent) {
    if (event.pointerId !== this.dragPointerId) return;
    this.dragging.set(false);
    this.dragPointerId = null;
  }

  @HostListener('window:resize')
  onWindowResize() {
    const pos = this.panelPos();
    const panel = this.panel?.nativeElement;
    if (!pos || !panel) return;
    const rect = panel.getBoundingClientRect();
    this.panelPos.set(this.clamp(pos.x, pos.y, rect.width, rect.height));
  }

  private clamp(x: number, y: number, w: number, h: number): { x: number; y: number } {
    const maxX = Math.max(0, window.innerWidth - w);
    const maxY = Math.max(0, window.innerHeight - h);
    return { x: Math.min(Math.max(0, x), maxX), y: Math.min(Math.max(0, y), maxY) };
  }
}
